import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Card, CardBody, Col, Input, Label, Row } from 'reactstrap';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/dist/sweetalert2.min.css';
import erorSound from '../../assets/audio/alert-error.wav';
import { useAuth } from '../../auth';
import Breadcrumb from '../../components/common/breadcrumb';
import api from '../../services/api-configuration';
import useFormatMessage from '../../utils/useFormatMessage';

// Terminal Programları > Atölye Giriş: scan a VIN to assign it directly into this warehouse's TYPE_WORKSHOP
// zone — the scan-terminal equivalent of doing the same assignment through Yerinde Adresleme's web panel
// (same backend calls: getAddressSuggestion + assignChassis), restricted to this one zone so an operator at
// the workshop entrance can't accidentally address a car anywhere else.
const AtolyeGirisTerminal = () => {
    const { t } = useFormatMessage();
    const { hasRole } = useAuth(7); // same permission group as Yerinde Adresleme (yerinde_adresleme_write)
    const [workshopZone, setWorkshopZone] = useState(null);
    const [zonesLoaded, setZonesLoaded] = useState(false);
    const [vin, setVin] = useState('');
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);

    const warehouseId = (() => {
        try { return parseInt(Buffer.from(localStorage.getItem('warehouse') || '', 'base64').toString('ascii'), 10) || 0; }
        catch { return 0; }
    })();

    useEffect(() => {
        if (!warehouseId) { setZonesLoaded(true); return; }
        api.post('/zone/getZonesByWarehouse', { warehouse_id: warehouseId })
            .then((response) => {
                const allData = response.data.data || [];
                const workshop = allData.find((z) => z.zone_type === 'TYPE_WORKSHOP');
                setWorkshopZone(workshop || null);
            })
            .catch(() => setWorkshopZone(null))
            .finally(() => setZonesLoaded(true));
        // eslint-disable-next-line
    }, []);

    useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, [zonesLoaded]);

    const submit = async (value) => {
        const chassisNo = (value || '').trim().toUpperCase();
        if (!hasRole('yerinde_adresleme_write')) {
            toast.error(t('Bu işlemi yapmaya yetkiniz yoktur.'));
            return;
        }
        if (!workshopZone) return;
        if (chassisNo.length !== 17) {
            toast.error(t('17 karakterli şase numarası giriniz'));
            return;
        }
        if (busy) return;
        setBusy(true);
        try {
            const suggestion = await api.post('/getAddressSuggestion', {
                // FILL_ALL checks UsedCapacity < the address's real configured Capacity; X_COUNT with x_count:1
                // would instead cap every address at exactly 1 vehicle regardless of its real capacity.
                algorithm_type: 'FILL_ALL',
                direction: 'START',
                scan_type: 'HORIZONTAL',
                zone_id: workshopZone.id,
                skip_address: '',
                last_selected_address: '',
            });
            const addressCode = suggestion.data.data.suggested_address;
            // getAddressSuggestion returns an empty suggested_address when this chassis is already in the
            // zone (no further address to assign) — same case Yerinde Adresleme's own validateFields() catches
            // before ever calling assignChassis; mirror that same clean message instead of sending an empty
            // address_code through and surfacing the backend's raw "address code is required" error.
            if (!addressCode) {
                toast.error(t('Please select an address'));
                setBusy(false);
                if (inputRef.current) inputRef.current.focus();
                return;
            }
            const response = await api.post('/assignChassis', {
                address_code: addressCode,
                chassis_no: chassisNo,
                zone_id: workshopZone.id,
            });
            // Pull in any currently-open devir-teslim (custody) damage for this VIN as an open workshop item, so
            // the technician sees it immediately without a manual "+ Hasar ekle" step. Best-effort: a failure here
            // must not undo or block the entry itself, which already succeeded above.
            let createdCount = 0;
            try {
                const auto = await api.post('/workshop/autoCreateFromDamage', { chassis_no: chassisNo });
                createdCount = auto.data.created || 0;
            } catch (autoErr) { /* entry already succeeded; silently skip, the damage stays available via "+ Hasar ekle" */ }
            const damageLine = createdCount > 0
                ? `<br/><span style="font-size: 16px; color:#b45309;">${createdCount} açık hasar kalemi atölyeye aktarıldı</span>` : '';
            await Swal.fire({
                icon: 'success',
                html: `<div style="font-size: 24px;"><b>${t('Adreslendi')}</b><br/><span style="font-size: 18px;">${response.data.message || addressCode}</span>${damageLine}</div>`,
                timer: 2500, showConfirmButton: false, position: 'center', allowOutsideClick: false,
                customClass: { popup: 'swal2-popup-large' },
            });
            setVin('');
        } catch (e) {
            if (navigator.vibrate) navigator.vibrate(500);
            const errorSound = document.getElementById('errorSound');
            if (errorSound) errorSound.play();
            const error = e?.response?.data?.error || e?.response?.data?.message || t('Beklenmedik bir hata oluştu');
            await Swal.fire({
                icon: 'error', title: 'HATA', html: `<div style="font-size: 24px;">${error}</div>`,
                confirmButtonText: 'OK', allowOutsideClick: false, allowEscapeKey: false, background: '#fff5f5',
                customClass: { popup: 'swal2-popup-large' },
            });
            setVin('');
        } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.focus();
        }
    };

    return (
        <div>
            <audio id="errorSound" src={erorSound} preload="auto"></audio>
            <Breadcrumb current={t('Atölye Giriş')} />
            <Card><CardBody>
                {zonesLoaded && !workshopZone && (
                    <Row><Col md="6"><div className="text-danger">{t('Bu depoda atölye bölgesi tanımlı değil')}</div></Col></Row>
                )}
                <Row className="mt-3">
                    <Col md="6">
                        <Label>{t('Şase')}</Label>
                        <Input innerRef={inputRef} value={vin} disabled={!workshopZone || busy}
                            placeholder={t('Okutunuz veya yazınız')}
                            onChange={(e) => setVin(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') submit(vin); }} />
                    </Col>
                </Row>
            </CardBody></Card>
        </div>
    );
};

export default AtolyeGirisTerminal;
